import { useCallback } from 'react';
import { useApiData, useAuthenticatedFetch } from '../client';
import type { Message, MessageCreateRequest, MessageUpdateRequest } from '../../types/media-sim';

export function useMessages(projectId: string, options?: { status?: string }) {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  const qs = params.toString();
  const endpoint = qs
    ? `/media-sim/projects/${projectId}/messages?${qs}`
    : `/media-sim/projects/${projectId}/messages`;
  return useApiData<Message[]>(endpoint);
}

export function useMessage(projectId: string, messageId: string) {
  return useApiData<Message>(`/media-sim/projects/${projectId}/messages/${messageId}`);
}

export function useCreateMessage() {
  const { fetchData } = useAuthenticatedFetch<Message>();
  const createMessage = useCallback(
    (projectId: string, data: MessageCreateRequest) =>
      fetchData(`/media-sim/projects/${projectId}/messages`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    [fetchData]
  );
  return { createMessage };
}

export function useUpdateMessage() {
  const { fetchData } = useAuthenticatedFetch<Message>();
  const updateMessage = useCallback(
    (projectId: string, messageId: string, data: MessageUpdateRequest) =>
      fetchData(`/media-sim/projects/${projectId}/messages/${messageId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    [fetchData]
  );
  return { updateMessage };
}

export function useRegisterMessage() {
  const { fetchData } = useAuthenticatedFetch<Message>();
  const registerMessage = useCallback(
    (projectId: string, messageId: string) =>
      fetchData(`/media-sim/projects/${projectId}/messages/${messageId}/register`, {
        method: 'POST',
      }),
    [fetchData]
  );
  return { registerMessage };
}

export function useDuplicateMessage() {
  const { fetchData } = useAuthenticatedFetch<Message>();
  const duplicateMessage = useCallback(
    (projectId: string, messageId: string) =>
      fetchData(`/media-sim/projects/${projectId}/messages/${messageId}/duplicate`, {
        method: 'POST',
      }),
    [fetchData]
  );
  return { duplicateMessage };
}
